import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Divider,
  IconButton,
  Chip,
} from "@mui/material";
import { HiChevronLeft, HiChevronRight, HiChevronDown } from "react-icons/hi";
import { 
  FiPlus, 
  FiKey, 
  FiEdit2, 
  FiTrash2, 
  FiPlay, 
  FiPause,
  FiStar,
  FiMessageSquare,
  FiHelpCircle
} from "react-icons/fi";
import ToggleSwitch from "../ToggleSwitch";
import CreateFlowModal from "./CreateFlowModal";
import UnsubscribeModal from "./UnsubscribeModal";
import EditDropdown from "../EditDropdown";
import FlowTypeModal from "../FlowTypeModal";
import ExplanationModal from "../ExplanationModal";
import apiEndpoints from "../../apiconfig";
import { useNavigate } from "react-router-dom";

const FlowPage = () => {
  const navigate = useNavigate();
  const [isCreateFlowOpen, setIsCreateFlowOpen] = useState(false);
  const [isUnsubscribeOpen, setIsUnsubscribeOpen] = useState(false);
  const [isFlowTypeModalOpen, setIsFlowTypeModalOpen] = useState(false);
  const [isExplanationModalOpen, setIsExplanationModalOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedExplanationNodeId, setSelectedExplanationNodeId] = useState(null);
  const [flowData, setFlowData] = useState([]);

  useEffect(() => {
    fetch(apiEndpoints.flow)
      .then((res) => res.json())
      .then((data) => setFlowData(data))
      .catch((err) => console.error("Error fetching flows:", err));
  }, []);

  // Core Functionalities
  const toggleStatus = (id) => {
    const flow = flowData.find((f) => f.id === id);
    if (!flow) return;
    const updatedFlow = { ...flow, status: flow.status ? 0 : 1 };
    fetch(`${apiEndpoints.flow}?id=${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedFlow),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setFlowData((prev) => prev.map((f) => (f.id === id ? updatedFlow : f)));
        }
      });
  };

  const toggleDefault = (id) => {
    const flow = flowData.find((f) => f.id === id);
    if (!flow) return;
    const updatedFlow = { ...flow, is_default: flow.is_default ? 0 : 1 };
    fetch(`${apiEndpoints.flow}?id=${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedFlow),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setFlowData((prev) => prev.map((f) => (f.id === id ? updatedFlow : f)));
        }
      });
  };

  const addFlow = (newFlow) => {
    fetch(apiEndpoints.flow, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newFlow),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setFlowData((prev) => [...prev, { ...newFlow, id: data.id }]);
        }
      });
  };

  const handleEditField = (id, field, value) => {
    const flow = flowData.find((f) => f.id === id);
    if (!flow) return;
    const updatedFlow = { ...flow, [field]: value };
    fetch(`${apiEndpoints.flow}?id=${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updatedFlow),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setFlowData((prev) => prev.map((f) => (f.id === id ? updatedFlow : f)));
        }
      });
  };

  const deleteFlow = (flowId) => {
    fetch(`${apiEndpoints.flow}?id=${flowId}`, { method: "DELETE" })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setFlowData((prev) => prev.filter((f) => f.id !== flowId));
        }
      });
  };

  const handleOpenFlowTypeModal = (id) => {
    setSelectedNodeId(id);
    setIsFlowTypeModalOpen(true);
  };

  const handleSaveKeywords = (value) => {
    if (selectedNodeId) handleEditField(selectedNodeId, "keywords", value);
    setIsFlowTypeModalOpen(false);
  };

  const handleOpenExplanationModal = (id) => {
    setSelectedExplanationNodeId(id);
    setIsExplanationModalOpen(true);
  };

  const handleSaveExplanation = (value) => {
    if (selectedExplanationNodeId)
      handleEditField(selectedExplanationNodeId, "explanation", value);
    setIsExplanationModalOpen(false);
  };

  const handleEditFlowChart = (flowId) => {
    navigate(`/flowchart?flowId=${flowId}`);
  };

  const getStatusChip = (status) => (
    <Chip
      label={status ? "Active" : "Inactive"}
      size="small"
      sx={{
        backgroundColor: status ? "#DCFCE7" : "#FEF2F2",
        color: status ? "#166534" : "#991B1B",
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    />
  );

  const getDefaultChip = (isDefault) => (
    <Chip
      icon={<FiStar size={14} />}
      label={isDefault ? "Default" : "Custom"}
      size="small"
      sx={{
        backgroundColor: isDefault ? "#FEF3C7" : "#F3F4F6",
        color: isDefault ? "#92400E" : "#6B7280",
        fontWeight: 600,
        fontSize: "0.75rem",
      }}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#F8FAFC",
        minHeight: "100vh",
        p: { xs: 2, sm: 3, md: 4 },
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      {/* Main Container */}
      <Box sx={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            mb: 4,
            gap: 2,
          }}
        >
          <Box>
            <Typography 
              variant="h4" 
              fontWeight={700} 
              color="#0F172A"
              sx={{ fontSize: { xs: '1.75rem', md: '2rem' } }}
            >
              Flow Management
            </Typography>
            <Typography 
              variant="body2" 
              color="#64748B"
              sx={{ mt: 0.5 }}
            >
              Design and manage automated conversation workflows
            </Typography>
          </Box>
          <Box 
            sx={{ 
              display: "flex", 
              alignItems: "center", 
              color: "#64748B",
              fontSize: "0.875rem"
            }}
          >
            <Typography color="#64748B">Home</Typography>
            <HiChevronRight style={{ margin: "0 4px" }} />
            <Typography color="#25D366" fontWeight={600}>Flow Management</Typography>
          </Box>
        </Box>

        {/* Controls Section */}
        <Card 
          sx={{ 
            borderRadius: "16px", 
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)", 
            mb: 4,
            border: "1px solid #F1F5F9"
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={6} lg={4}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography 
                    fontWeight={600} 
                    color="#374151"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Matching Type:
                  </Typography>
                  <Box sx={{ position: "relative", flex: 1 }}>
                    <select
                      style={{
                        width: "100%",
                        border: "1px solid #E5E7EB",
                        borderRadius: "12px",
                        padding: "12px 16px",
                        color: "#374151",
                        fontWeight: 500,
                        background: "#fff",
                        fontSize: "14px",
                        appearance: "none",
                        cursor: "pointer",
                      }}
                    >
                      <option>Fuzzy Matching</option>
                      <option>Exact Matching</option>
                      <option>Pattern Matching</option>
                    </select>
                    <HiChevronDown 
                      style={{ 
                        position: "absolute", 
                        right: "12px", 
                        top: "50%", 
                        transform: "translateY(-50%)",
                        color: "#6B7280",
                        pointerEvents: "none"
                      }} 
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6} lg={8}>
                <Box 
                  sx={{ 
                    display: "flex", 
                    gap: 2, 
                    flexWrap: "wrap",
                    justifyContent: { xs: "flex-start", md: "flex-end" }
                  }}
                >
                  <Button
                    variant="contained"
                    startIcon={<FiPlus />}
                    sx={{
                      bgcolor: "#25D366",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "12px",
                      px: 3,
                      py: 1.5,
                      fontSize: "14px",
                      "&:hover": { 
                        bgcolor: "#22C35E",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)"
                      },
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setIsCreateFlowOpen(true)}
                  >
                    Create Flow
                  </Button>
                  {/* <Button
                    variant="outlined"
                    startIcon={<FiKey />}
                    sx={{
                      borderColor: "#E2E8F0",
                      color: "#64748B",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "12px",
                      px: 3,
                      py: 1.5,
                      fontSize: "14px",
                      "&:hover": { 
                        borderColor: "#CBD5E1",
                        bgcolor: "#F8FAFC",
                        transform: "translateY(-1px)"
                      },
                      transition: "all 0.2s ease",
                    }}
                    onClick={() => setIsUnsubscribeOpen(true)}
                  >
                    Unsubscribe Keys
                  </Button> */}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Card 
              sx={{ 
                borderRadius: "16px",
                bgcolor: "white",
                border: "1px solid #F1F5F9",
                p: 3,
                textAlign: "center"
              }}
            >
              <Typography variant="h4" fontWeight={700} color="#25D366">
                {flowData.length}
              </Typography>
              <Typography variant="body2" color="#64748B" sx={{ mt: 1 }}>
                Total Flows
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card 
              sx={{ 
                borderRadius: "16px",
                bgcolor: "white",
                border: "1px solid #F1F5F9",
                p: 3,
                textAlign: "center"
              }}
            >
              <Typography variant="h4" fontWeight={700} color="#3B82F6">
                {flowData.filter(f => f.status).length}
              </Typography>
              <Typography variant="body2" color="#64748B" sx={{ mt: 1 }}>
                Active Flows
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card 
              sx={{ 
                borderRadius: "16px",
                bgcolor: "white",
                border: "1px solid #F1F5F9",
                p: 3,
                textAlign: "center"
              }}
            >
              <Typography variant="h4" fontWeight={700} color="#F59E0B">
                {flowData.filter(f => f.is_default).length}
              </Typography>
              <Typography variant="body2" color="#64748B" sx={{ mt: 1 }}>
                Default Flows
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card 
              sx={{ 
                borderRadius: "16px",
                bgcolor: "white",
                border: "1px solid #F1F5F9",
                p: 3,
                textAlign: "center"
              }}
            >
              <Typography variant="h4" fontWeight={700} color="#8B5CF6">
                {flowData.filter(f => !f.status).length}
              </Typography>
              <Typography variant="body2" color="#64748B" sx={{ mt: 1 }}>
                Inactive Flows
              </Typography>
            </Card>
          </Grid>
        </Grid>

        {/* Table Section */}
        <Card 
          sx={{ 
            borderRadius: "16px", 
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #F1F5F9",
            overflow: "hidden"
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h6" fontWeight={600} color="#0F172A" sx={{ mb: 1 }}>
                Flow List
              </Typography>
              <Typography variant="body2" color="#64748B" sx={{ mb: 2 }}>
                Manage your conversation flows and automation rules
              </Typography>
            </Box>
            <Divider />
            
            {/* Responsive Table Container */}
            <Box sx={{ overflowX: "auto" }}>
              <TableContainer>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                    <TableRow>
                      {[
                        { label: "S.No", width: "80px" },
                        { label: "Flow Name", width: "200px" },
                        { label: "Flow Type", width: "120px" },
                        { label: "Status", width: "100px" },
                        { label: "Default", width: "100px" },
                        { label: "Keywords", width: "150px" },
                        { label: "Explanation", width: "150px" },
                        { label: "Actions", width: "120px" },
                      ].map((header) => (
                        <TableCell
                          key={header.label}
                          sx={{
                            fontWeight: 600,
                            color: "#374151",
                            fontSize: "0.875rem",
                            py: 2,
                            width: header.width,
                            borderBottom: "1px solid #F1F5F9"
                          }}
                        >
                          {header.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {flowData.map((flow, index) => (
                      <TableRow 
                        key={flow.id} 
                        hover
                        sx={{ 
                          '&:last-child td': { borderBottom: 0 },
                          transition: "background-color 0.2s ease"
                        }}
                      >
                        <TableCell sx={{ py: 2.5, color: "#64748B", fontWeight: 500 }}>
                          {index + 1}
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <FiMessageSquare size={16} color="#64748B" />
                            <Typography 
                              fontWeight={600} 
                              color="#0F172A"
                              sx={{ fontSize: "0.875rem" }}
                            >
                              {flow.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Chip
                            label={flow.type || "Standard"}
                            size="small"
                            sx={{
                              backgroundColor: "#EFF6FF",
                              color: "#1E40AF",
                              fontWeight: 500,
                              fontSize: "0.75rem"
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <ToggleSwitch
                              isOn={!!Number(flow.status)}
                              onToggle={() => toggleStatus(flow.id)}
                            />
                            {getStatusChip(!!Number(flow.status))}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <ToggleSwitch
                              isOn={!!Number(flow.is_default)}
                              onToggle={() => toggleDefault(flow.id)}
                            />
                            {getDefaultChip(!!Number(flow.is_default))}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FiEdit2 size={14} />}
                            onClick={() => handleOpenFlowTypeModal(flow.id)}
                            sx={{
                              borderColor: "#E2E8F0",
                              color: "#475569",
                              textTransform: "none",
                              borderRadius: "8px",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              px: 2,
                              "&:hover": {
                                borderColor: "#CBD5E1",
                                bgcolor: "#F8FAFC"
                              }
                            }}
                          >
                            {flow.keywords ? "Edit Keywords" : "Add Keywords"}
                          </Button>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FiHelpCircle size={14} />}
                            onClick={() => handleOpenExplanationModal(flow.id)}
                            sx={{
                              borderColor: "#E2E8F0",
                              color: "#475569",
                              textTransform: "none",
                              borderRadius: "8px",
                              fontSize: "0.75rem",
                              fontWeight: 500,
                              px: 2,
                              "&:hover": {
                                borderColor: "#CBD5E1",
                                bgcolor: "#F8FAFC"
                              }
                            }}
                          >
                            {flow.explanation ? "Edit Notes" : "Add Notes"}
                          </Button>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <EditDropdown
                            flowId={flow.id}
                            onDelete={deleteFlow}
                            onEditFlowChart={() => handleEditFlowChart(flow.id)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Empty State */}
            {flowData.length === 0 && (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <FiMessageSquare size={48} color="#E5E7EB" />
                <Typography color="#6B7280" sx={{ mt: 2, mb: 1 }}>
                  No flows created yet
                </Typography>
                <Typography variant="body2" color="#9CA3AF">
                  Get started by creating your first conversation flow
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {flowData.length > 0 && (
          <Box 
            sx={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center", 
              mt: 4,
              flexDirection: { xs: "column", sm: "row" },
              gap: 2
            }}
          >
            <Typography variant="body2" color="#64748B">
              Showing {flowData.length} of {flowData.length} flows
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                sx={{
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  color: "#64748B",
                  "&:hover": { bgcolor: "#F8FAFC" }
                }}
              >
                <HiChevronLeft />
              </IconButton>
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: "#25D366",
                  color: "white",
                  minWidth: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#22C35E" }
                }}
              >
                1
              </Button>
              <IconButton
                size="small"
                sx={{
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  color: "#64748B",
                  "&:hover": { bgcolor: "#F8FAFC" }
                }}
              >
                <HiChevronRight />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>

      {/* Modals */}
      <CreateFlowModal
        isOpen={isCreateFlowOpen}
        onClose={() => setIsCreateFlowOpen(false)}
        addFlow={addFlow}
      />
      <UnsubscribeModal
        isOpen={isUnsubscribeOpen}
        onClose={() => setIsUnsubscribeOpen(false)}
      />
      <FlowTypeModal
        isOpen={isFlowTypeModalOpen}
        onClose={() => setIsFlowTypeModalOpen(false)}
        onSave={handleSaveKeywords}
      />
      <ExplanationModal
        isOpen={isExplanationModalOpen}
        onClose={() => setIsExplanationModalOpen(false)}
        onSave={handleSaveExplanation}
        initialValue={
          flowData.find((f) => f.id === selectedExplanationNodeId)?.explanation || ""
        }
      />
    </Box>
  );
};

export default FlowPage;